import React, { useMemo, useState } from 'react'
import { useTranslation } from "react-i18next"
import TextField from "@material-ui/core/TextField"
import MenuItem from '@material-ui/core/MenuItem'
import Chip from "@material-ui/core/Chip"
import InputMask from "react-input-mask"
import brIcon from "../../../../assets/br.svg"
import usIcon from "../../../../assets/us.svg"
import Tooltip from "@material-ui/core/Tooltip"
import Button from '@mui/material/Button'
import Menu from '@material-ui/core/Menu'
import DeleteIcon from '@material-ui/icons/Delete'
import { translateType, validateConstantValues, getConstantTextFields, colorVelvet } from "./utils"
import VariableWhiteIcon from "../../../../assets/rulesIcon/variable_white.svg"
import ConstantNumberWhite from "../../../../assets/rulesIcon/constant_number_white.svg"
import RestoreWhite from "../../../../assets/rulesIcon/restore_white.svg"

export function getRegionFromEnvironment(environment) {
    if (environment === RULE_PIECE_CURRENT_EVENT_ENVIRONMENT) return RULE_PIECE_CURRENT_EVENT_REGION
    if (environment === "production_sa" || environment === "production-sa") return "sa-east-1"
    return "us-east-1"
}

export function getEnvironmentFromRegion(region, ruleEnvironment) {
    if (region === RULE_PIECE_CURRENT_EVENT_REGION) return RULE_PIECE_CURRENT_EVENT_ENVIRONMENT
    if (region === "sa-east-1") return "production_sa"
    return ruleEnvironment === "sandbox" ? "sandbox" : "production"
}

export const RULE_PIECE_CURRENT_COMPANY_KEY = "rule_piece_current_company"

export const RULE_PIECE_CURRENT_EVENT_ENUM = "rule_piece_current_event"
export const RULE_PIECE_CURRENT_EVENT_ES_INDEX = "rule_piece_current_index"
export const RULE_PIECE_CURRENT_EVENT_I18N_KEY = "Evento da Regra Atual"
export const RULE_PIECE_CURRENT_EVENT_ENVIRONMENT = "rule_piece_current_environment"
export const RULE_PIECE_CURRENT_EVENT_REGION = "rule_piece_current_region"
export const RULE_PIECE_CURRENT_EVENT_DATE_PATH = "rule_piece_current_event_date_path"

export function createRulePieceCurrentPickerEventType() {
    return {
        enum: RULE_PIECE_CURRENT_EVENT_ENUM,
        es_index: RULE_PIECE_CURRENT_EVENT_ES_INDEX,
        description: RULE_PIECE_CURRENT_EVENT_I18N_KEY,
        environment: RULE_PIECE_CURRENT_EVENT_ENVIRONMENT,
        event_date_path: RULE_PIECE_CURRENT_EVENT_DATE_PATH
    }
}

function fieldRowHasConfiguredTargetPath(field) {
    if (field == null) return false
    const path = field.target_field_path
    if (path == null) return false
    if (typeof path === 'string') return path.trim() !== ''
    if (Array.isArray(path)) return path.length > 0
    return true
}

export function areFieldsCleared(fields) {
    const list = Array.isArray(fields) ? fields : []
    if (list.length === 0) return true
    if (list.length > 1) return false
    return !fieldRowHasConfiguredTargetPath(list[0])
}

export function getEventTypesIntersectionAcrossCompanies(companyList) {
    const companies = (companyList || []).filter(c => c != null)
    if (companies.length === 0) return []

    const eventSignature = eventType =>
        `${eventType.enum}$#$${getRegionFromEnvironment(eventType.environment)}`

    const uniqueEventTypesBySignature = comp => {
        const seen = new Set()
        const bySig = new Map()
        for (const ev of comp.company_events || []) {
            const et = ev.field_mapping?.event_type
            if (!et) continue
            const sig = eventSignature(et)
            if (seen.has(sig)) continue
            seen.add(sig)
            bySig.set(sig, et)
        }
        return bySig
    }

    const firstMap = uniqueEventTypesBySignature(companies[0])
    if (firstMap.size === 0) return []

    let commonSignatures = new Set(firstMap.keys())
    for (let i = 1; i < companies.length; i++) {
        const map = uniqueEventTypesBySignature(companies[i])
        commonSignatures = new Set(
            [...commonSignatures].filter(sig => map.has(sig))
        )
    }

    return [...commonSignatures].map(sig => firstMap.get(sig))
}

export function isDatasetEntryMatchingTargetEvent(
    datasetValue,
    selectedRegion,
    selectedEnum,
) {
    if (
        datasetValue?.region == null ||
        datasetValue?.target_event_enum == null ||
        selectedRegion == null ||
        selectedEnum == null
    ) {
        return false
    }
    return (
        datasetValue.region === selectedRegion &&
        datasetValue.target_event_enum === selectedEnum
    )
}

function mappingPathSetFromMappings(mappings) {
    const list = Array.isArray(mappings) ? mappings : []
    return new Set(list.map(m => m?.path).filter((item) => !!item))
}

function isDatasetEntryCoveredByMappings(datasetValue, pathSet) {
    const extraPaths = datasetValue?.additional_field_paths || []
    return extraPaths.every(p => pathSet.has(p))
}

export function filterMatchingTargetEvent(
    datasetsObject,
    selectedRegion,
    selectedEnum,
) {
    const entries = []
    for (const [key, value] of Object.entries(datasetsObject || {})) {
        if (
            isDatasetEntryMatchingTargetEvent(
                value,
                selectedRegion,
                selectedEnum,
            )
        ) {
            entries.push({ key, value })
        }
    }
    return entries
}

export function filterByMappings(entriesWithKeyValue, mappings) {
    const pathSet = mappingPathSetFromMappings(mappings)
    const filtered = []
    for (const { key, value } of entriesWithKeyValue) {
        if (isDatasetEntryCoveredByMappings(value, pathSet)) {
            filtered.push({ key, description: value?.description ?? key })
        }
    }
    return filtered
}

export function filterDatasetEntries(
    datasetsObject,
    mappings,
    selectedRegion,
    selectedEnum,
) {
    const afterTargetEvent = filterMatchingTargetEvent(
        datasetsObject,
        selectedRegion,
        selectedEnum,
    )
    return filterByMappings(afterTargetEvent, mappings)
}

export function isCachedDatasetCompatibleWithMappings(
    datasetKey,
    datasetsObject,
    mappings,
    selectedRegion,
    selectedEnum,
) {
    if (!datasetKey || !datasetsObject?.[datasetKey]) return false
    const entry = datasetsObject[datasetKey]
    if (
        !isDatasetEntryMatchingTargetEvent(
            entry,
            selectedRegion,
            selectedEnum,
        )
    ) {
        return false
    }
    const pathSet = mappingPathSetFromMappings(mappings)
    return isDatasetEntryCoveredByMappings(entry, pathSet)
}

export function HistoricalDataPicker(props) {
    const { t } = useTranslation()
    const {
        targetCompanyKey,
        targetEventType,
        companyList,
        companyEvents,
        ruleEventType,
        isRulePiece,
        readOnly,
        enableBusinessGroupQueryOption,
        onCompanyKeyChange,
        onEventTypeChange,
        showRulePieceNoSharedEventTypesHint,
    } = props

    const pickerCompanyEvents = useMemo(() => {
        const sortedBase = [...(companyEvents || [])].sort((a, b) =>
            (a.description || "").localeCompare(b.description || "")
        )
        if (!isRulePiece) return sortedBase
        return [
            createRulePieceCurrentPickerEventType(),
            ...sortedBase,
        ]
    }, [companyEvents, isRulePiece])

    const targetRegion = targetEventType?.region ?? getRegionFromEnvironment(targetEventType?.environment ?? "")
    const eventValue = targetEventType?.enum != null && targetRegion
        ? `${targetEventType.enum}$#$${targetRegion}`
        : ""

    const uniqueRegions = new Set(pickerCompanyEvents
        .map(e => getRegionFromEnvironment(e?.environment))
        .filter(r => r !== RULE_PIECE_CURRENT_EVENT_REGION)
    )
    const showRegionIcons = uniqueRegions.size > 1

    const currentRuleEventChip = (
        <Chip
            label={t(RULE_PIECE_CURRENT_EVENT_I18N_KEY)}
            size="small"
            style={{
                backgroundColor: "#0b1f821a",
                color: "#0b1f82",
                fontWeight: "bold",
                marginLeft: "12px"
            }}
        />
    )

    const renderRegionIcon = (eventType) => {
        if (!eventType?.environment ||
            eventType.environment === RULE_PIECE_CURRENT_EVENT_ENVIRONMENT) return null
        const regionToIcon = { "us-east-1": usIcon, "sa-east-1": brIcon }
        const region = getRegionFromEnvironment(eventType.environment)
        const iconSrc = regionToIcon[region]
        if (!iconSrc) return null
        return (
            <img
                src={iconSrc}
                alt={region}
                style={{ width: 16, height: 16, borderRadius: "2px", marginLeft: "8px" }}
            />
        )
    }

    const sortedCompanies = [...(companyList || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    const currentCompanyOption = {
        name: "Esteira da Regra Atual",
        company_key: RULE_PIECE_CURRENT_COMPANY_KEY,
    }
    
    const businessGroupOption = {
        name: "Todos no Grupo de Negócios",
        company_key: "every_company"
    }

    let companyOptions = sortedCompanies
    if (enableBusinessGroupQueryOption) {
        companyOptions = [businessGroupOption, ...companyOptions]
    }
    if (isRulePiece) {
        companyOptions = [currentCompanyOption, ...companyOptions]
    }

    return (
        <>
            <p className={["blueText", "subtitleSize"].join(" ")} style={{ width: "100%", margin: "0", marginTop: "12px" }}>
                {t("Selecionar Base Histórica")}
            </p>
            {enableBusinessGroupQueryOption && (
                <TextField
                    label={t("Cliente")}
                    className="filterTextbox"
                    size="small"
                    fullWidth
                    value={targetCompanyKey ?? ""}
                    onChange={(e) => onCompanyKeyChange(e, e.target.value)}
                    select
                    InputProps={{ readOnly }}
                    style={{ marginTop: "12px" }}
                >
                    {companyOptions.map((companyItem, idx) => (
                        <MenuItem key={idx} value={companyItem.company_key}>
                            <em className="normalText">{t(companyItem.name)}</em>
                        </MenuItem>
                    ))}
                </TextField>
            )}
            {showRulePieceNoSharedEventTypesHint && (
                <p
                    className={["normalText", "normalSmallSize"].join(" ")}
                    style={{ margin: "8px 0 0 0", color: "#666" }}
                >
                    {t(
                        "Sem tipos de evento comuns a todas as esteiras neste trecho."
                    )}
                </p>
            )}
            <TextField
                label={t("Tipo de Evento")}
                className="filterTextbox"
                size="small"
                fullWidth
                value={eventValue}
                onChange={(e) => {
                    const [selectedEnum, selectedRegion] = (e.target.value || "").split("$#$")
                    const selectedEvent = pickerCompanyEvents.find(
                        event => event?.enum === selectedEnum && getRegionFromEnvironment(event?.environment) === selectedRegion
                    )
                    onEventTypeChange(e, selectedEvent ?? null)
                }}
                select
                InputProps={{ readOnly }}
                style={{ marginTop: "12px" }}
                SelectProps={{
                    renderValue: (selectedValue) => {
                        const [selectedEnum, selectedRegion] = (selectedValue || "").split("$#$")
                        const event = pickerCompanyEvents.find(
                            e => e?.enum === selectedEnum && getRegionFromEnvironment(e?.environment) === selectedRegion
                        )
                        const isCurrentEvent = !isRulePiece && selectedEnum === ruleEventType?.enum &&
                            getRegionFromEnvironment(ruleEventType?.environment) === selectedRegion
                        return (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                                <em className="normalText">{event ? t(event.description) : ""}</em>
                                {isCurrentEvent && currentRuleEventChip}
                                {showRegionIcons && renderRegionIcon(event)}
                            </div>
                        )
                    }
                }}
                error={!targetEventType?.enum}
            >
                {pickerCompanyEvents.map((event, idx) => {
                    const isCurrentEvent = !isRulePiece && event?.enum === ruleEventType?.enum &&
                        getRegionFromEnvironment(event?.environment) === getRegionFromEnvironment(ruleEventType?.environment)
                    return (
                        <MenuItem
                            key={idx}
                            value={`${event?.enum ?? ""}$#$${getRegionFromEnvironment(event?.environment)}`}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                            <em className="normalText">{t(event?.description ?? "")}</em>
                            {isCurrentEvent && currentRuleEventChip}
                            {showRegionIcons && renderRegionIcon(event)}
                        </MenuItem>
                    )
                })}
            </TextField>
        </>
    )
}

export function toIsoDateTime(formattedTime) {
    if (!formattedTime) return formattedTime
    if (formattedTime.length < 19) return formattedTime
    const year   = formattedTime.substring(6, 10)
    const month  = formattedTime.substring(3, 5)
    const day    = formattedTime.substring(0, 2)
    const hour   = formattedTime.substring(11, 13)
    const minute = formattedTime.substring(14, 16)
    const second = formattedTime.substring(17, 19)
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`
}

export function toFormattedDateTime(isoTime) {
    if (!isoTime) return isoTime
    if (isoTime.length < 24) return isoTime
    const year   = isoTime.substring(0, 4)
    const month  = isoTime.substring(5, 7)
    const day    = isoTime.substring(8, 10)
    const hour   = isoTime.substring(11, 13)
    const minute = isoTime.substring(14, 16)
    const second = isoTime.substring(17, 19)
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`
}

export function FieldsManager(props) {
    const { t } = useTranslation()
    const {
        read_only,
        fields,
        onUpdate,
        rulesList,
        fieldMapping,
        useAdvancedMode,
        sectionTitle
    } = props

    const handleAdd = () => {
        onUpdate([...fields, { target_field_path: "", comparison: null, value: null, type: "dynamic" }])
    }

    const handleRemove = (index) => {
        const newFields = [...fields]
        newFields.splice(index, 1)
        onUpdate(newFields)
    }

    const handleUpdate = (index, updateData) => {
        const newFields = [...fields]
        newFields[index] = { ...newFields[index], ...updateData }
        onUpdate(newFields)
    }

    const getComparisonSetForType = (type) => {
        const completeComparisonSet = [
            { value: "greater", label: "Maior que" },
            { value: "less", label: "Menor que" },
            { value: "greater_or_equal", label: "Maior ou igual a" },
            { value: "less_or_equal", label: "Menor ou igual a" },
            { value: "equal", label: "Igual a" },
            { value: "different", label: "Diferente de" }
        ]
        const reducedComparisonSet = [
            { value: "equal", label: "Igual a" },
            { value: "different", label: "Diferente de" }
        ]
        switch (type) {
            case "string":
                return reducedComparisonSet
            case "bool":
                return reducedComparisonSet
            case "datetime":
                return completeComparisonSet
            default:
                return completeComparisonSet
        }
    }

    const stringifyField = (f) => `${f.target_field_path},${f.comparison},${f.value},${f.type}`
    const duplicateSignatures = new Set(
        fields.map(stringifyField).filter((sig, index, arr) => arr.indexOf(sig) !== index)
    )

    const title = sectionTitle != null ? sectionTitle : t('fields')

    return (
        <>
            <p className={["blueText", "subtitleSize"].join(" ")} style={{ width: "100%", margin: "12px 0 6px 0" }}>{title}</p>
            {fields.map((field, index) => {
                const mappings = fieldMapping?.fetchedData?.mappings || []
                const mappingsForSelect = [...mappings]
                    .filter((mappingField) => (mappingField.type === "number" || mappingField.type === "string" || mappingField.type === "datetime") && mappingField.elasticsearch_type !== "none")
                    .sort((a, b) => (a.description || "").localeCompare(b.description || ""))
                const pathVariableType = mappings.find(item => item.path === field.target_field_path)?.type ?? null

                const currentEventFields = rulesList.fields_mapper_list || []
                const possibleMappings = currentEventFields.filter((mapping) => {
                    if (useAdvancedMode) {
                        return !pathVariableType || mapping.type === pathVariableType
                    }
                    return mapping.path === field.target_field_path
                })

                const comparisonSet = getComparisonSetForType(pathVariableType)
                const isDuplicate = duplicateSignatures.has(stringifyField(field))
                const isFieldMappingAvailable = fieldMapping?.fetchedData?.mappings && !fieldMapping?.isLoading

                return (
                    <div style={{ display: "flex", gap: 0, width: "100%", margin: "6px 0", alignItems: "center", minWidth: 0 }} key={`field-${index}`}>
                        <div style={{ display: "flex", flexDirection: "column", margin: "3px auto", width: "100%", minWidth: 0 }}>
                            <div style={{ display: "flex", width: "100%", alignItems: "center", minWidth: 0 }}>
                                <TextField
                                    label={t("Campo da Base Histórica")}
                                    error={isFieldMappingAvailable && (field.target_field_path === "" || field.target_field_path == null || isDuplicate)}
                                    className="filterTextbox"
                                    size="small"
                                    fullWidth
                                    value={!isFieldMappingAvailable ? "loading" : (field?.target_field_path ?? "")}
                                    onChange={(e) => {
                                        handleUpdate(index, {
                                            target_field_path: e.target.value,
                                            comparison: null,
                                            value: (!useAdvancedMode && field.type === "dynamic") ? e.target.value : null
                                        })
                                    }}
                                    select
                                    InputProps={{
                                        readOnly: read_only,
                                        style: { overflow: "hidden", textOverflow: "ellipsis" }
                                    }}
                                    disabled={!isFieldMappingAvailable || read_only}
                                >
                                    {!isFieldMappingAvailable ? (
                                        <MenuItem value="loading">
                                            <em className="normalText">{t("Carregando...")}</em>
                                        </MenuItem>
                                    ) : (
                                        mappingsForSelect.map((mappingField, field_index) => (
                                            <MenuItem key={`target-field-${field_index}`} value={mappingField.path}>
                                                <em className="normalText">{t(mappingField.description) + " (" + t(translateType(mappingField.type)) + ")"}</em>
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>
                            </div>
                            <TextField
                                label={t("Comparação")}
                                error={field?.target_field_path && (field?.comparison === "" || field?.comparison == null || isDuplicate)}
                                className="filterTextbox"
                                size="small"
                                fullWidth
                                select
                                value={field?.comparison || ""}
                                onChange={(e) => handleUpdate(index, { comparison: e.target.value })}
                                disabled={!fieldMapping?.fetchedData?.mappings || fieldMapping.isLoading || !field?.target_field_path || field?.target_field_path === "" || read_only}
                                InputProps={{ readOnly: read_only }}
                            >
                                {[...comparisonSet].sort((a, b) => (a.label || "").localeCompare(b.label || "")).map((element) => (
                                    <MenuItem key={`comparison-${element.value}`} value={element.value}><em className="normalText">{t(element.label)}</em></MenuItem>
                                ))}
                            </TextField>
                            <RowValueInput
                                field={field}
                                handleUpdate={(newField) => handleUpdate(index, newField)}
                                pathVariableType={pathVariableType}
                                userVariables={props.userVariables}
                                read_only={read_only}
                                possibleMappings={possibleMappings}
                                isTargetFieldDisabled={!fieldMapping?.fetchedData?.mappings}
                                useAdvancedMode={useAdvancedMode}
                                isDuplicate={isDuplicate}
                            />
                        </div>
                        {!read_only && (
                            <Tooltip title={t("Remover Campo")}>
                                <DeleteIcon
                                    style={{ cursor: "pointer", margin: "5px", height: "fit-content", fill: colorVelvet }}
                                    onClick={() => handleRemove(index)}
                                />
                            </Tooltip>
                        )}
                    </div>
                )
            })}
            {!read_only && (
                <div
                    style={{ cursor: "pointer", margin: "5px auto" }}
                    onClick={handleAdd}
                    className={["blueText", "normalSMallSize"].join(" ")}
                >
                    {t("+ ADICIONAR NOVO")}
                </div>
            )}
        </>
    )
}

export function RowValueInput(props) {
    const { t } = useTranslation()
    const {
        handleUpdate,
        field,
        read_only,
        pathVariableType,
        userVariables,
        possibleMappings,
        isTargetFieldDisabled,
        useAdvancedMode,
        isDuplicate
    } = props

    const [valueTypeMenuAnchorEl, setValueTypeMenuAnchorEl] = useState(null)

    const handleValueTypeMenuClick = (event) => {
        setValueTypeMenuAnchorEl(event.currentTarget)
    }

    const handleValueTypeChange = (newType) => {
        const defaultTarget = (!useAdvancedMode && newType === "dynamic") ? field.target_field_path : null
        handleUpdate({
            type: newType,
            value: defaultTarget,
        })
        setValueTypeMenuAnchorEl(null)
    }

    const valueTypeToIcon = {
        static: ConstantNumberWhite,
        user_variable: VariableWhiteIcon,
        dynamic: RestoreWhite
    }
    const type = field?.type
    const comparison = field?.comparison

    const getConstantValueInput = (inputType) => {
        switch (inputType) {
            case "number":
                return (
                    <TextField
                        label={t('Valor')}
                        error={comparison && (!validateConstantValues("number", field.value) || isDuplicate)}
                        value={field?.value || ""}
                        className="filterTextBox"
                        style={{ height: 48 }}
                        size="small"
                        fullWidth
                        type="number"
                        onChange={(e) => handleUpdate({ value: Number(e.target.value) })}
                        InputProps={{ readOnly: read_only }}
                        disabled={isTargetFieldDisabled || !comparison || read_only}
                    />
                )
            case "datetime":
                const datetimeError = comparison && !validateConstantValues("datetime", field.value ? field.value : false)
                return (
                    <InputMask
                        label={t('Valor')}
                        style={{ textAlign: "center", width: "100%", margin: "3px auto", height: 48 }}
                        error={datetimeError || isDuplicate}
                        value={toFormattedDateTime(field?.value) || ""}
                        {...getConstantTextFields('datetime').props}
                        onChange={(e) => handleUpdate({ value: toIsoDateTime(e.target.value) })}
                        InputProps={{ readOnly: read_only }}
                        disabled={isTargetFieldDisabled || !comparison || read_only}
                        helperText={datetimeError ? t("Formato esperado: DD/MM/AAAA HH:MM:SS") : null}
                    />
                )
            case "string":
                return (
                    <TextField
                        label={t('Valor')}
                        error={comparison && (!validateConstantValues("string", field.value) || isDuplicate)}
                        value={field?.value || ""}
                        className="filterTextBox"
                        style={{ height: 48 }}
                        size="small"
                        fullWidth
                        type="string"
                        onChange={(e) => handleUpdate({ value: e.target.value })}
                        InputProps={{ readOnly: read_only }}
                        disabled={isTargetFieldDisabled || !comparison || read_only}
                    />
                )
            default:
                return (
                    <TextField
                        label={t('Valor')}
                        error={comparison && isDuplicate}
                        value={field?.value || ""}
                        className="filterTextBox"
                        style={{ height: 48 }}
                        size="small"
                        fullWidth
                        type="string"
                        onChange={(e) => handleUpdate({ value: e.target.value })}
                        InputProps={{ readOnly: read_only }}
                        disabled={isTargetFieldDisabled || !comparison || read_only}
                    />
                )
        }
    }

    const renderInput = () => {
        switch (type) {
            case "dynamic":
                if (!useAdvancedMode) {
                    const selectedMapping = possibleMappings[0]
                    const displayValue = selectedMapping
                        ? `${t(selectedMapping.description)} (${t(translateType(selectedMapping.type))})`
                        : field.value || ""
                    return (
                        <TextField
                            error={false}
                            className="filterTextbox"
                            size="small"
                            fullWidth
                            label={t("Campo do Evento Analisado")}
                            value={displayValue}
                            disabled
                        />
                    )
                }
                return (
                    <TextField
                        error={comparison && (field?.value == null || field?.value === "" || isDuplicate)}
                        className="filterTextbox"
                        size="small"
                        fullWidth
                        label={t("Campo do Evento Analisado")}
                        value={field?.value ?? ""}
                        select
                        onChange={(e) => handleUpdate({ value: e.target.value })}
                        disabled={isTargetFieldDisabled || !comparison || read_only}
                    >
                        {possibleMappings.length === 0 ? (
                            <MenuItem value="" disabled>
                                <em className="normalText">{t("Nenhum campo disponível para comparação")}</em>
                            </MenuItem>
                        ) : (
                            [...possibleMappings].sort((a, b) => (a.description || "").localeCompare(b.description || "")).map((mapping) => (
                                <MenuItem key={mapping.path} value={mapping.path}>
                                    <em className="normalText">
                                        {`${t(mapping.description)} (${t(translateType(mapping.type))})`}
                                    </em>
                                </MenuItem>
                            ))
                        )}
                    </TextField>
                )
            case "user_variable":
                return (
                    <TextField
                        label={t('Variável')}
                        error={comparison && (field?.value == null || field?.value === "" || isDuplicate)}
                        value={field.value ?? ""}
                        style={{ textAlign: "center" }}
                        select
                        fullWidth
                        onChange={(e) => handleUpdate({ value: e.target.value })}
                        disabled={isTargetFieldDisabled || !comparison || read_only}
                    >
                        {[...(userVariables || [])]
                            .filter((variable) => variable.variable_type === pathVariableType)
                            .sort((a, b) => (a.variable_name || "").localeCompare(b.variable_name || ""))
                            .map((variable, index) => (
                                <MenuItem key={index} value={variable.variable_name}>{variable.variable_name}</MenuItem>
                            ))}
                    </TextField>
                )
            case "static":
                return getConstantValueInput(pathVariableType)
            default:
                return <></>
        }
    }

    return (
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, minHeight: 48 }}>
            {renderInput()}
            <Tooltip title="Escolher tipo de Valor">
                <span>
                    <Button
                        variant='contained'
                        sx={[{ '&:hover': { backgroundColor: "#020f4d" } }, { maxWidth: 24, height: "32px", backgroundColor: "#0b1f82" }]}
                        onClick={handleValueTypeMenuClick}
                        disabled={!comparison || read_only}
                    >
                        <img src={valueTypeToIcon[type]} style={{ width: 20, height: 20 }} alt="Escolher tipo de Valor" />
                    </Button>
                </span>
            </Tooltip>
            <Menu
                anchorEl={valueTypeMenuAnchorEl}
                open={Boolean(valueTypeMenuAnchorEl)}
                onClose={() => setValueTypeMenuAnchorEl(null)}
            >
                <MenuItem onClick={() => handleValueTypeChange("static")}>
                    {t("Valor Constante")}
                </MenuItem>
                <MenuItem onClick={() => handleValueTypeChange("user_variable")}>
                    {t("Variável de Usuário")}
                </MenuItem>
                <MenuItem onClick={() => handleValueTypeChange("dynamic")}>
                    {t("Campo do Evento Analisado")}
                </MenuItem>
            </Menu>
        </div>
    )
}
